import React, { useState, useRef, useCallback } from 'react';
import {
  Upload, X, File, Image, FileText, Video, Music, Archive,
  Download, Eye, Trash2, AlertTriangle, CheckCircle, Clock,
  RefreshCw, FolderOpen, Search, Filter, Grid, List, MoreHorizontal
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import toast from 'react-hot-toast';
import { useAuthStore } from '@/store/auth';
import { apiClient } from '@/api/client';

interface FileItem {
  id: string;
  name: string;
  size: number;
  type: string;
  file?: File;
  url?: string;
  uploadProgress?: number;
  status: 'pending' | 'uploading' | 'completed' | 'error';
  error?: string;
  uploadedAt?: string;
  category?: string;
  tags?: string[];
  description?: string;
  isPublic?: boolean;
  thumbnail?: string;
}

interface FileUploadProps {
  maxFiles?: number;
  maxSize?: number; // in bytes
  acceptedTypes?: string[];
  uploadEndpoint?: string;
  onFilesUploaded?: (files: FileItem[]) => void;
  onFileDeleted?: (fileId: string) => void;
  allowMultiple?: boolean;
  showFileList?: boolean;
  compact?: boolean;
  category?: string;
}

const FileUpload: React.FC<FileUploadProps> = ({
  maxFiles = 10,
  maxSize = 10 * 1024 * 1024, // 10MB default
  acceptedTypes = ['image/*', 'application/pdf', 'text/*', '.doc', '.docx', '.xls', '.xlsx'],
  uploadEndpoint = '/api/v1/files/upload',
  onFilesUploaded,
  onFileDeleted,
  allowMultiple = true,
  showFileList = true,
  compact = false,
  category = 'general'
}) => {
  const [files, setFiles] = useState<FileItem[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // File type validation and icon mapping
  const getFileIcon = (type: string, name: string) => {
    if (type.startsWith('image/')) return Image;
    if (type.startsWith('video/')) return Video;
    if (type.startsWith('audio/')) return Music;
    if (type.includes('pdf')) return FileText;
    if (type.includes('zip') || type.includes('rar') || type.includes('7z')) return Archive;
    return File;
  };

  const getFileCategory = (type: string): string => {
    if (type.startsWith('image/')) return 'images';
    if (type.startsWith('video/')) return 'videos';
    if (type.startsWith('audio/')) return 'audio';
    if (type.includes('pdf') || type.includes('doc') || type.includes('txt')) return 'documents';
    if (type.includes('zip') || type.includes('rar') || type.includes('7z')) return 'archives';
    return 'others';
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const validateFile = (file: File): string | null => {
    // Size validation
    if (file.size > maxSize) {
      return `Dosya boyutu ${formatFileSize(maxSize)} boyutunu geçemez`;
    }

    // Type validation
    const isValidType = acceptedTypes.some(type => {
      if (type.startsWith('.')) {
        return file.name.toLowerCase().endsWith(type.toLowerCase());
      }
      if (type.includes('*')) {
        const baseType = type.split('/')[0];
        return file.type.startsWith(baseType);
      }
      return file.type === type;
    });

    if (!isValidType) {
      return `Desteklenmeyen dosya türü. İzin verilen türler: ${acceptedTypes.join(', ')}`;
    }

    // Max files validation
    if (!allowMultiple && files.length >= 1) {
      return 'Sadece bir dosya yükleyebilirsiniz';
    }

    if (files.length >= maxFiles) {
      return `Maksimum ${maxFiles} dosya yükleyebilirsiniz`;
    }

    return null;
  };

  const processFiles = useCallback((selectedFiles: FileList | File[]) => {
    const fileArray = Array.from(selectedFiles);
    const newFiles: FileItem[] = [];

    fileArray.forEach((file) => {
      const validationError = validateFile(file);
      
      if (validationError) {
        toast.error(validationError);
        return;
      }

      const fileItem: FileItem = {
        id: `file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        name: file.name,
        size: file.size,
        type: file.type,
        file,
        status: 'pending',
        category: getFileCategory(file.type)
      };

      newFiles.push(fileItem);
    });

    if (newFiles.length > 0) {
      setFiles(prev => allowMultiple ? [...prev, ...newFiles] : newFiles);
      // Start uploading immediately
      newFiles.forEach(fileItem => uploadFile(fileItem));
    }
  }, [files, allowMultiple, maxFiles, maxSize, acceptedTypes]);

  const uploadFile = async (fileItem: FileItem) => {
    if (!fileItem.file) return;

    // Update status to uploading
    setFiles(prev => prev.map(f => 
      f.id === fileItem.id ? { ...f, status: 'uploading', uploadProgress: 0 } : f
    ));

    try {
      const formData = new FormData();
      formData.append('file', fileItem.file);
      formData.append('category', category);
      formData.append('description', fileItem.description || '');
      formData.append('tags', JSON.stringify(fileItem.tags || []));
      formData.append('isPublic', String(fileItem.isPublic || false));

      // Use axios client so auth header is injected by interceptor
      const response = await apiClient.post(uploadEndpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const result = response.data;
      const fileMeta = result.file || result;

      // Build uploaded item from fresh meta
      const uploadedItem: FileItem = {
        ...fileItem,
        status: 'completed',
        uploadProgress: 100,
        url: fileMeta.url,
        uploadedAt: new Date().toISOString(),
        thumbnail: fileMeta.thumbnail_url,
      };

      // Update file with server response in local list
      setFiles(prev => prev.map(f => 
        f.id === fileItem.id ? uploadedItem : f
      ));

      toast.success(`${fileItem.name} başarıyla yüklendi`);

      // Callback for successful upload (use fresh object, not stale state)
      if (onFilesUploaded) {
        onFilesUploaded([uploadedItem]);
      }

    } catch (error: any) {
      console.error('Upload error:', error);
      const message = error?.response?.status === 401 ? 'Unauthorized' : (error?.message || 'Upload failed');
      setFiles(prev => prev.map(f => 
        f.id === fileItem.id ? {
          ...f,
          status: 'error',
          error: message
        } : f
      ));

      toast.error(`${fileItem.name} yüklenemedi: ${message}`);
    }
  };

  const removeFile = (fileId: string) => {
    const fileToRemove = files.find(f => f.id === fileId);
    
    setFiles(prev => prev.filter(f => f.id !== fileId));
    
    if (fileToRemove?.url && onFileDeleted) {
      onFileDeleted(fileId);
    }
    
    toast('Dosya kaldırıldı');
  };

  const retryUpload = (fileId: string) => {
    const fileItem = files.find(f => f.id === fileId);
    if (fileItem && fileItem.status === 'error') {
      uploadFile(fileItem);
    }
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles.length > 0) {
      processFiles(droppedFiles);
    }
  }, [processFiles]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (selectedFiles && selectedFiles.length > 0) {
      processFiles(selectedFiles);
    }
    // Reset input value to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  const downloadFile = (fileItem: FileItem) => {
    if (fileItem.url) {
      const link = document.createElement('a');
      link.href = fileItem.url;
      link.download = fileItem.name;
      link.click();
    }
  };

  const previewFile = (fileItem: FileItem) => {
    if (fileItem.url && fileItem.type.startsWith('image/')) {
      window.open(fileItem.url, '_blank');
    } else if (fileItem.url) {
      window.open(fileItem.url, '_blank');
    }
  };

  // Filter files based on search and filter
  const filteredFiles = files.filter(file => {
    const matchesSearch = file.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filter === 'all' || file.category === filter || file.status === filter;
    return matchesSearch && matchesFilter;
  });

  const getStatusColor = (status: FileItem['status']) => {
    switch (status) {
      case 'completed': return 'text-green-600';
      case 'uploading': return 'text-primary-600';
      case 'error': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const getStatusIcon = (status: FileItem['status']) => {
    switch (status) {
      case 'completed': return CheckCircle;
      case 'uploading': return RefreshCw;
      case 'error': return AlertTriangle;
      default: return Clock;
    }
  };

  if (compact) {
    return (
      <div className="space-y-4">
        {/* Compact Upload Area */}
        <div
          className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
            isDragging 
              ? 'border-primary-500 bg-primary-50' 
              : 'border-gray-300 hover:border-gray-400'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={openFileDialog}
        >
          <Upload className="w-6 h-6 mx-auto mb-2 text-gray-400" />
          <p className="text-sm text-gray-600">
            Dosya yüklemek için tıklayın veya sürükleyin
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Max {formatFileSize(maxSize)} • {acceptedTypes.join(', ')}
          </p>
        </div>

        {/* Compact File List */}
        {files.length > 0 && (
          <div className="space-y-2">
            {files.map((file) => {
              const IconComponent = getFileIcon(file.type, file.name);
              const StatusIcon = getStatusIcon(file.status);
              
              return (
                <div key={file.id} className="flex items-center space-x-3 p-2 bg-gray-50 rounded">
                  <IconComponent className="w-4 h-4 text-gray-500" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                    <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <StatusIcon className={`w-4 h-4 ${getStatusColor(file.status)}`} />
                    <button
                      onClick={() => removeFile(file.id)}
                      className="text-gray-400 hover:text-red-500"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          multiple={allowMultiple}
          accept={acceptedTypes.join(',')}
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Upload Area */}
      <div
        className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all duration-200 ${
          isDragging 
            ? 'border-primary-500 bg-primary-50 scale-105' 
            : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={openFileDialog}
      >
        <Upload className={`w-12 h-12 mx-auto mb-4 ${isDragging ? 'text-primary-500' : 'text-gray-400'}`} />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          {isDragging ? 'Dosyaları bırakın' : 'Dosya yükleyin'}
        </h3>
        <p className="text-gray-600 mb-4">
          Dosyaları buraya sürükleyin veya seçmek için tıklayın
        </p>
        <div className="space-y-2 text-sm text-gray-500">
          <p>Maksimum dosya boyutu: {formatFileSize(maxSize)}</p>
          <p>Desteklenen formatlar: {acceptedTypes.join(', ')}</p>
          <p>Maksimum dosya sayısı: {maxFiles}</p>
        </div>
      </div>

      {/* File Management Controls */}
      {showFileList && files.length > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between space-y-4 sm:space-y-0">
          <div className="flex items-center space-x-4">
            <h3 className="text-lg font-medium text-gray-900">
              Yüklenen Dosyalar ({files.length})
            </h3>
            <div className="flex space-x-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded ${viewMode === 'grid' ? 'bg-primary-100 text-primary-600' : 'text-gray-400'}`}
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded ${viewMode === 'list' ? 'bg-primary-100 text-primary-600' : 'text-gray-400'}`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Dosya ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            {/* Filter */}
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="all">Tümü</option>
              <option value="images">Resimler</option>
              <option value="documents">Belgeler</option>
              <option value="videos">Videolar</option>
              <option value="audio">Ses</option>
              <option value="archives">Arşivler</option>
              <option value="completed">Tamamlanan</option>
              <option value="uploading">Yükleniyor</option>
              <option value="error">Hatalı</option>
            </select>
          </div>
        </div>
      )}

      {/* File List */}
      {showFileList && filteredFiles.length > 0 && (
        <div className={viewMode === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4' : 'space-y-4'}>
          {filteredFiles.map((file) => {
            const IconComponent = getFileIcon(file.type, file.name);
            const StatusIcon = getStatusIcon(file.status);
            
            return (
              <div
                key={file.id}
                className={`border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow ${
                  viewMode === 'list' ? 'flex items-center space-x-4' : ''
                }`}
              >
                {/* File Preview/Icon */}
                <div className={`${viewMode === 'grid' ? 'mb-4' : ''} relative`}>
                  {file.thumbnail ? (
                    <img
                      src={file.thumbnail}
                      alt={file.name}
                      className={`${viewMode === 'grid' ? 'w-full h-32' : 'w-16 h-16'} object-cover rounded`}
                    />
                  ) : (
                    <div className={`${viewMode === 'grid' ? 'w-full h-32' : 'w-16 h-16'} bg-gray-100 rounded flex items-center justify-center`}>
                      <IconComponent className="w-8 h-8 text-gray-400" />
                    </div>
                  )}
                  {/* Status badge */}
                  <div className="absolute top-2 right-2">
                    <StatusIcon className={`w-5 h-5 ${getStatusColor(file.status)}`} />
                  </div>
                </div>

                {/* File Info */}
                <div className={`${viewMode === 'grid' ? '' : 'flex-1'}`}>
                  <h4 className="font-medium text-gray-900 truncate mb-1">{file.name}</h4>
                  <p className="text-sm text-gray-500 mb-2">{formatFileSize(file.size)} • {file.category}</p>

                  {/* Upload Progress */}
                  {file.status === 'uploading' && file.uploadProgress !== undefined && (
                    <div className="mb-3">
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-primary-600 h-2 rounded-full transition-all duration-300" style={{ width: `${file.uploadProgress}%` }} />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">{file.uploadProgress}%</p>
                    </div>
                  )}

                  {/* Error message */}
                  {file.status === 'error' && file.error && (
                    <p className="text-sm text-red-600 mb-2">{file.error}</p>
                  )}

                  {/* Actions */}
                  <div className="flex items-center space-x-2">
                    {file.status === 'completed' && (
                      <>
                        <Button onClick={() => previewFile(file)} size="sm" variant="outline" className="text-xs">
                          <Eye className="w-3 h-3 mr-1" />
                          Önizle
                        </Button>
                        <Button onClick={() => downloadFile(file)} size="sm" variant="outline" className="text-xs">
                          <Download className="w-3 h-3 mr-1" />
                          İndir
                        </Button>
                      </>
                    )}

                    {file.status === 'error' && (
                      <Button onClick={() => retryUpload(file.id)} size="sm" variant="outline" className="text-xs text-primary-600">
                        <RefreshCw className="w-3 h-3 mr-1" />
                        Tekrar Dene
                      </Button>
                    )}

                    <Button onClick={() => removeFile(file.id)} size="sm" variant="outline" className="text-xs text-red-600 hover:bg-red-50">
                      <Trash2 className="w-3 h-3 mr-1" />
                      Sil
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Empty state */}
      {showFileList && filteredFiles.length === 0 && files.length > 0 && (
        <div className="text-center py-8">
          <FolderOpen className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <p className="text-gray-600">Filtre kriterleriyle eşleşen dosya bulunamadı</p>
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple={allowMultiple}
        accept={acceptedTypes.join(',')}
        onChange={handleFileSelect}
        className="hidden"
      />
    </div>
  );
};

export default FileUpload;